import { contactLink, siteTitle, useDocumentTitle } from "@1-now/booking-core";
import { FC, FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { requestTrackCode, verifyTrackCode } from "api/booking/trackBooking";
import { useData } from "data/data-provider";
import { globalJson } from "global/global_json";
import Input from "shared/Input/Input";
import { buildContactRows } from "utils/contactRows";
import { formatPhone, phoneHref } from "utils/formatPhone";
import {
  CODE_LENGTH,
  RESEND_AFTER_SECONDS,
  TRACK_BOOKING_LABEL,
  TrackedBooking,
  cleanCode,
  forgetTracked,
  isEmail,
  maskEmail,
  readTracked,
  saveTracked,
  statusTone,
  tripDays,
} from "utils/trackBooking";

/**
 * Track my booking (1NOW-11157), shared by Designs A and B.
 *
 * Three steps: the renter gives the email they booked with, types the six-digit
 * code we mailed to it, and sees all their bookings (upcoming and under way first, then
 * the ones that are over, each with its status), each a link to its own booking page. One column on a phone (each step replaces the last), two from lg
 * up (the step on the left, the results on the right). The site's header and
 * footer come from the router. Colours are the site's theme tokens; only the
 * status chips and the error banner use fixed green, amber, grey and red, which mean
 * the same on every site.
 *
 * What the renter typed is never in the URL, and the form and the results are marked
 * `ph-no-capture` so PostHog autocapture records neither. The verified list is also kept
 * for this tab (readTracked), so Back from a booking shows it again without a new code.
 */

const labelCls = "block text-sm font-semibold text-text-color mb-1.5";
const errorCls = "mt-1.5 text-xs font-medium text-red-600";
/** 125 → "2:05": the wait between codes runs to minutes. */
const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const eyebrowCls = "text-xs font-extrabold uppercase tracking-widest text-secondary-text-color";
const buttonCls =
  "inline-flex min-w-[180px] items-center justify-center rounded-full bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary px-7 py-3 text-sm font-extrabold text-white shadow-sm transition-all duration-200 hover:bg-primary-brand hover:text-on-primary disabled:opacity-70";
// Faiza (7 Oct): the email and code fields need a visible edge. The shared Input's border is
// secondary-100, which on dark palettes is the card colour, so the boxes vanished.
const fieldEdgeCls = "!border-text-color/50 bg-bg-color placeholder:text-text-color/50 focus:!border-primary-600";
const linkButtonCls = "text-sm font-semibold text-text-color underline disabled:no-underline disabled:opacity-60";

const CHIP: Record<"green" | "amber" | "gray", string> = {
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  gray: "bg-gray-100 text-gray-700",
};

const CarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-6 w-6" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 16.5V19m14-2.5V19M3.5 13l1.6-4.6A2 2 0 0 1 7 7h10a2 2 0 0 1 1.9 1.4L20.5 13M3.5 13h17M3.5 13v3.5h17V13M7.5 15.5h.01M16.5 15.5h.01" />
  </svg>
);

const Spinner = () => (
  <span className="spinner-border inline-block h-5 w-5 animate-spin rounded-full border-4 border-gray-300 border-t-transparent" />
);

const ResultRow: FC<{ booking: TrackedBooking }> = ({ booking }) => {
  const days = tripDays(booking.pick_up_time, booking.drop_off_time, booking.timezone);
  return (
    <li className="!mb-0">
      {/* 1NOW-11157 (8 Oct): a normal push, flagged so the booking page lets Back return here (its Back goes home
          after paying). Router state, not the URL. */}
      <Link
        to={`/booking-details?id=${booking.unique_identifier}`}
        state={{ fromTrackBooking: true }}
        className="flex flex-col gap-2.5 rounded-2xl border border-secondary-100 bg-card-color p-3.5 transition-colors hover:border-primary-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-brand sm:flex-row sm:items-center sm:gap-4"
      >
        <span className="flex min-w-0 items-center gap-3 sm:flex-1">
          <span className="flex h-11 w-14 shrink-0 items-center justify-center rounded-lg bg-secondary-50 text-secondary-text-color ring-1 ring-secondary-100 sm:h-14 sm:w-[90px]">
            <CarIcon />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-bold text-text-color">{booking.car_name}</span>
            <span className="block text-xs text-secondary-text-color">
              <span className="sm:hidden">{days.short}</span>
              <span className="hidden sm:inline">{days.long}</span>
              {" · "}#{booking.booking_number}
            </span>
          </span>
        </span>
        <span className="flex items-center justify-between gap-3 sm:justify-end">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-extrabold ${CHIP[statusTone(booking.status)]}`}>
            {booking.status_label}
          </span>
          <span className="whitespace-nowrap text-sm font-bold text-text-color">View &rsaquo;</span>
        </span>
      </Link>
    </li>
  );
};

const Banner: FC<{ children: ReactNode }> = ({ children }) => (
  <div role="alert" className="mb-5 rounded-2xl bg-red-50 p-3.5 text-sm font-bold text-red-800">
    {children}
  </div>
);

type Step = "email" | "code" | "results";

const TrackBookingPage: FC = () => {
  const { companyDetails, contactItems } = useData();
  useDocumentTitle(companyDetails ? siteTitle(companyDetails, TRACK_BOOKING_LABEL) : undefined);

  // 1NOW-11157 (8 Oct): back from a booking, the list verified earlier in this tab, if it is still fresh.
  const [kept] = useState(() => readTracked(globalJson.company_id));
  const [step, setStep] = useState<Step>(kept ? "results" : "email");
  const [email, setEmail] = useState(kept?.email ?? "");
  const [emailError, setEmailError] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [notice, setNotice] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [bookings, setBookings] = useState<TrackedBooking[]>(kept?.bookings ?? []);
  const resultsRef = useRef<HTMLElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [codeFocused, setCodeFocused] = useState(false);

  // The resend wait: counts down while the code step is open.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  // The operator's own phone and email: the footer rows first, the company's
  // own as the fallback, the way the Contact page reads them.
  const help = useMemo(() => {
    const rows = buildContactRows(contactItems);
    const phoneRow = rows.find((r) => r.type === "phone");
    const emailRow = rows.find((r) => r.type === "email");
    // 1NOW-11211: the company's own number formatted like the Contact page ("+92 309 5182731").
    const companyPhone = companyDetails?.company_phone
      ? { label: formatPhone(String(companyDetails.company_phone)), href: phoneHref(String(companyDetails.company_phone)) || null }
      : null;
    const companyEmail = companyDetails?.company_email ? contactLink(String(companyDetails.company_email)) : null;
    return {
      phone: phoneRow ? { label: phoneRow.display, href: phoneRow.href } : companyPhone,
      email: emailRow ? { label: emailRow.display, href: emailRow.href } : companyEmail,
    };
  }, [contactItems, companyDetails]);

  const phoneLink = help.phone && (
    <a className="font-bold underline" href={help.phone.href ?? undefined}>
      {help.phone.label}
    </a>
  );
  const emailLink = help.email && (
    <a className="font-bold underline" href={help.email.href ?? undefined}>
      {help.email.label}
    </a>
  );

  const sendCode = async (again: boolean) => {
    setProblem("");
    setNotice("");
    const result = await requestTrackCode(email.trim());
    if ("sent" in result) {
      forgetTracked(globalJson.company_id);
      setStep("code");
      setCode("");
      setSecondsLeft(result.resendAfter ?? RESEND_AFTER_SECONDS);
      if (result.alreadySent) setNotice("We've already sent you a code. Check your email.");
      else if (again) setNotice("We sent you a new code.");
      setTimeout(() => codeRef.current?.focus(), 0);
    } else {
      setProblem(result.error);
      if (result.resendAfter) setSecondsLeft(result.resendAfter);
    }
  };

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!isEmail(email)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError("");
    setBusy(true);
    await sendCode(false);
    setBusy(false);
  };

  const resend = async () => {
    if (busy || secondsLeft > 0) return;
    setBusy(true);
    await sendCode(true);
    setBusy(false);
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (code.length !== CODE_LENGTH) {
      setProblem(`Enter the ${CODE_LENGTH}-digit code.`);
      return;
    }
    setProblem("");
    setNotice("");
    setBusy(true);
    const result = await verifyTrackCode(email.trim(), code);
    setBusy(false);
    if ("bookings" in result) {
      saveTracked(globalJson.company_id, email.trim(), result.bookings);
      setBookings(result.bookings);
      setCode("");
      setStep("results");
      // Phone: the results replace the step, so bring them to the top.
      setTimeout(() => {
        resultsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
        resultsRef.current?.focus();
      }, 0);
    } else if ("badCode" in result) {
      setProblem("That code isn't right or has expired.");
    } else {
      setProblem(result.error);
    }
  };

  const useAnotherEmail = () => {
    forgetTracked(globalJson.company_id);
    setStep("email");
    setCode("");
    setProblem("");
    setNotice("");
    setSecondsLeft(0);
  };

  const showingResults = step === "results";

  return (
    <div className="nc-TrackBookingPage bg-bg-color" data-nc-id="TrackBookingPage">
      <div className="container min-h-[60vh] py-8 sm:py-12 lg:py-16">
        {/* 1NOW-11157 (Faiza, 7 Oct): `minmax(0, ...)`, not a bare `fr`. A bare `fr` track never goes below its
            content's width, and a row's car name is `truncate` (nowrap), so one long name stretched the results
            column past the screen and pushed every "View" off the right edge, out of reach. */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-start lg:gap-14">
          {/* ----- The email, then the code. Hidden on a phone once there are results. ----- */}
          <section className={showingResults ? "hidden lg:block" : ""} aria-labelledby="track-booking-heading">
            <p className={eyebrowCls}>Your trip</p>
            <h1
              id="track-booking-heading"
              className="mt-1 text-2xl font-extrabold uppercase leading-tight tracking-tight text-text-color sm:text-3xl lg:mt-2 lg:text-4xl"
            >
              {step === "code" ? "Check your email" : TRACK_BOOKING_LABEL}
            </h1>

            {step === "code" ? (
              <p className="mb-5 mt-2 max-w-md text-sm leading-relaxed text-secondary-text-color lg:mb-6 lg:text-base">
                We sent a code to <span className="font-bold text-text-color">{maskEmail(email)}</span>. It works for
                10 minutes.
              </p>
            ) : (
              <p className="mb-5 mt-2 max-w-md text-sm leading-relaxed text-secondary-text-color lg:mb-6 lg:text-base">
                <span className="lg:hidden">Enter the email you booked with and we will send you a code.</span>
                <span className="hidden lg:inline">
                  Enter the email you booked with and we will send you a code to see your booking, pay what is due, or
                  verify your ID.
                </span>
              </p>
            )}

            {problem && <Banner>{problem}</Banner>}
            {notice && !problem && (
              <p role="status" className="mb-5 text-sm font-semibold text-text-color">
                {notice}
              </p>
            )}

            {step !== "code" ? (
              <form
                onSubmit={submitEmail}
                noValidate
                className="ph-no-capture rounded-3xl border border-secondary-100 bg-card-color p-5 shadow-sm lg:p-6"
              >
                <label className={labelCls} htmlFor="track-email">
                  Email
                </label>
                <Input
                  id="track-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={`mt-0 ${emailError ? "!border-red-500 bg-bg-color" : fieldEdgeCls}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? "track-email-error" : undefined}
                />
                {emailError && (
                  <p id="track-email-error" className={errorCls}>
                    {emailError}
                  </p>
                )}
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                  <button type="submit" disabled={busy} className={buttonCls}>
                    {busy ? <Spinner /> : "Send code"}
                  </button>
                  <p className="text-xs text-secondary-text-color">We only show bookings made with this email.</p>
                </div>
              </form>
            ) : (
              <form
                onSubmit={submitCode}
                noValidate
                className="ph-no-capture rounded-3xl border border-secondary-100 bg-card-color p-5 shadow-sm lg:p-6"
              >
                <label className={labelCls} htmlFor="track-code">
                  6-digit code
                </label>
                {/* 1NOW-11211: six boxes, OTP style. One real input lies over them (invisible), so
                    typing, Backspace, paste and the phone's one-time-code autofill all work as
                    on any field; the boxes only draw it, the next one highlighted. */}
                <div className="relative w-full max-w-[22rem]">
                  <div className="grid grid-cols-6 gap-1.5 sm:gap-2" aria-hidden="true" data-testid="code-boxes">
                    {Array.from({ length: CODE_LENGTH }, (_, i) => {
                      const active = codeFocused && i === Math.min(code.length, CODE_LENGTH - 1);
                      return (
                        <span
                          key={i}
                          data-active={active || undefined}
                          className={`flex h-12 items-center justify-center rounded-xl border bg-bg-color text-xl font-bold text-text-color sm:h-14 sm:text-2xl ${
                            problem ? "border-red-500" : active ? "border-primary-600 ring-2 ring-primary-600/30" : "border-text-color/50"
                          }`}
                        >
                          {code[i] ?? ""}
                        </span>
                      );
                    })}
                  </div>
                  <input
                    id="track-code"
                    name="code"
                    ref={codeRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={CODE_LENGTH}
                    value={code}
                    onChange={(e) => setCode(cleanCode(e.target.value))}
                    onFocus={() => setCodeFocused(true)}
                    onBlur={() => setCodeFocused(false)}
                    aria-invalid={!!problem}
                    className="absolute inset-0 h-full w-full cursor-text border-0 bg-transparent text-transparent caret-transparent opacity-0 focus:ring-0"
                  />
                </div>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                  <button type="submit" disabled={busy} className={buttonCls}>
                    {busy ? <Spinner /> : "Show my bookings"}
                  </button>
                  <button type="button" onClick={resend} disabled={busy || secondsLeft > 0} className={linkButtonCls}>
                    {secondsLeft > 0 ? `Resend code in ${clock(secondsLeft)}` : "Resend code"}
                  </button>
                </div>
                <button type="button" onClick={useAnotherEmail} className={`mt-4 ${linkButtonCls}`}>
                  Use a different email
                </button>
              </form>
            )}

            {!showingResults && (help.phone || help.email) && (
              <p className="mt-4 text-sm text-secondary-text-color">
                {"Can't find your booking? "}
                {help.phone && <>Call {phoneLink}</>}
                {help.phone && help.email && " or "}
                {help.email && <>email {emailLink}</>}.
              </p>
            )}
          </section>

          {/* ----- Results. On a phone they take the step's place. ----- */}
          <section
            ref={resultsRef}
            tabIndex={-1}
            aria-live="polite"
            aria-label="Your bookings"
            className={`ph-no-capture focus:outline-none ${showingResults ? "" : "hidden lg:block"}`}
          >
            {showingResults ? (
              <>
                <button type="button" onClick={useAnotherEmail} className={`mb-3 lg:hidden ${linkButtonCls}`}>
                  &lsaquo; Use a different email
                </button>
                <h2 className="text-2xl font-extrabold uppercase tracking-tight text-text-color lg:hidden">
                  Your bookings
                </h2>
                {bookings.length > 0 ? (
                  <>
                    <p className={`hidden lg:block ${eyebrowCls}`}>
                      {bookings.length} {bookings.length === 1 ? "booking" : "bookings"} found
                    </p>
                    <ul className="mt-3 list-none space-y-2.5 !ml-0 p-0">
                      {bookings.map((b) => (
                        <ResultRow key={b.unique_identifier} booking={b} />
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="mt-3 text-sm text-secondary-text-color">
                    There are no bookings for this email.
                    {help.phone && <> If you expected one, call us at {phoneLink}.</>}
                  </p>
                )}
                {bookings.length > 0 && (help.phone || help.email) && (
                  <div className="mt-4 hidden rounded-2xl bg-secondary-50 p-3.5 text-sm text-text-color ring-1 ring-secondary-100 lg:block">
                    {"Can't find it? "}
                    {help.phone && <>Call {phoneLink}</>}
                    {help.phone && help.email && " or "}
                    {help.email && <>email {emailLink}</>}.
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-secondary-100 p-6 text-sm text-secondary-text-color">
                Your bookings will show here.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TrackBookingPage;
